import { useState, useEffect } from 'react';
import { Euro, Search, Building2, Check, X, User, Calendar, ArrowRight, Percent, Filter } from 'lucide-react';
import { Collab, Influencer, getDocuments, updateDocument } from '../lib/supabase';
import { Dialog } from '@headlessui/react';

interface InfluencerPayment {
  fullName: string;
  campaignName: string;
  brand: string;
  rate: string;
  date: string | Date | { seconds: number };
  paid: boolean;
  campaignId: string;
  paidPercentage?: number;
}

type DateFilter = 'all' | 'week' | 'month' | '3months' | '6months' | 'year' | 'custom';

export default function Accounting() {
  const [collabs, setCollabs] = useState<Collab[]>([]);
  const [search, setSearch] = useState('');
  const [isLoading, setIsLoading] = useState(true);
  const [payments, setPayments] = useState<InfluencerPayment[]>([]);
  const [editingPayment, setEditingPayment] = useState<string | null>(null);
  const [tempPercentage, setTempPercentage] = useState<number>(0);
  const [dateFilter, setDateFilter] = useState<DateFilter>('all');
  const [startDate, setStartDate] = useState<string>('');
  const [endDate, setEndDate] = useState<string>('');
  const [isFilterModalOpen, setIsFilterModalOpen] = useState(false);

  useEffect(() => {
    loadData(); 
  }, []);

  const loadData = async () => {
    try {
      setIsLoading(true);
      const collabsData = await getDocuments<Collab>('collabs');
      setCollabs(collabsData);

      // Extract payments from paid campaigns
      const paymentsList: InfluencerPayment[] = [];
      collabsData
        .filter(collab => collab.payment_received)
        .forEach(collab => {
          if (collab.influencers && Array.isArray(collab.influencers)) {
            collab.influencers.forEach((influencer: any) => {
              const fullName = influencer.full_name || influencer.fullName || 'Nom inconnu';
              paymentsList.push({
                fullName,
                campaignName: collab.name || 'Campagne inconnue',
                brand: collab.brand || 'Marque inconnue',
                rate: influencer.rate || '0',
                date: collab.date,
                paid: influencer.creator_invoice_sent || false,
                campaignId: collab.id!,
                paidPercentage: influencer.paid_percentage || influencer.paidPercentage || 0
              });
            });
          }
        });

      // Trier les paiements par date (du plus récent au plus ancien)
      const sortedPayments = paymentsList.sort((a, b) => {
        // Convertir les dates en timestamps pour la comparaison
        const dateA = a.date ? 
          (typeof a.date === 'object' && 'seconds' in a.date) ? 
            a.date.seconds * 1000 : 
            new Date(a.date).getTime() : 
          0;
        
        const dateB = b.date ? 
          (typeof b.date === 'object' && 'seconds' in b.date) ? 
            b.date.seconds * 1000 : 
            new Date(b.date).getTime() : 
          0;
        
        // Tri décroissant (plus récent en premier)
        return dateB - dateA;
      });

      setPayments(sortedPayments);
      setIsLoading(false);
    } catch (error) {
      console.error('Error loading data:', error);
      setIsLoading(false);
    }
  };

  const handleUpdatePercentage = async (payment: InfluencerPayment, newPercentage: number, updatePaidStatus = false) => {
    try {
      const collab = collabs.find(c => c.id === payment.campaignId);
      if (!collab) return;

      // Ensure percentage is between 0 and 100
      newPercentage = Math.min(100, Math.max(0, newPercentage));

      const updatedInfluencers = collab.influencers.map((inf: any) => {
        const infFullName = inf.full_name || inf.fullName;
        if (infFullName === payment.fullName) {
          return {
            ...inf,
            creator_invoice_sent: updatePaidStatus ? newPercentage === 100 : inf.creator_invoice_sent,
            paid_percentage: newPercentage,
            paidPercentage: newPercentage
          };
        }
        return inf;
      });

      await updateDocument('collabs', payment.campaignId, {
        influencers: updatedInfluencers
      });

      await loadData();
      setEditingPayment(null);
    } catch (error) {
      console.error('Error updating payment percentage:', error);
    }
  };

  const handleTogglePaidStatus = async (payment: InfluencerPayment) => {
    try {
      const collab = collabs.find(c => c.id === payment.campaignId);
      if (!collab) return;

      const updatedInfluencers = collab.influencers.map((inf: any) => {
        const infFullName = inf.full_name || inf.fullName;
        if (infFullName === payment.fullName) {
          return {
            ...inf,
            creator_invoice_sent: !inf.creator_invoice_sent,
            paid_percentage: inf.paid_percentage || inf.paidPercentage || 0,
            paidPercentage: inf.paid_percentage || inf.paidPercentage || 0
          };
        }
        return inf;
      });

      await updateDocument('collabs', payment.campaignId, {
        influencers: updatedInfluencers
      });

      await loadData();
    } catch (error) {
      console.error('Error toggling paid status:', error);
    }
  };

  const formatDate = (date: string | Date | { seconds: number } | undefined) => {
    if (!date) return 'Date inconnue';

    try {
      // Handle string dates (ISO format)
      if (typeof date === 'string') {
        return new Date(date).toLocaleDateString('fr-FR', {
          day: 'numeric',
          month: 'long',
          year: 'numeric'
        });
      }

      if (typeof date === 'object' && date !== null && 'seconds' in date) {
        return new Date(date.seconds * 1000).toLocaleDateString('fr-FR', {
          day: 'numeric',
          month: 'long',
          year: 'numeric'
        });
      }

      return new Date(date).toLocaleDateString('fr-FR', {
        day: 'numeric',
        month: 'long',
        year: 'numeric'
      });
    } catch (error) {
      console.error('Error formatting date:', error, date);
      return 'Date invalide';
    }
  };

  const getDateFromSeconds = (date: { seconds: number }) => {
    return new Date(date.seconds * 1000);
  };

  const isDateInRange = (date: Date | { seconds: number } | string) => {
    let paymentDate: Date;
    
    // Handle string dates (ISO format)
    if (typeof date === 'string') {
      paymentDate = new Date(date);
    } else if ('seconds' in date) {
      paymentDate = getDateFromSeconds(date);
    } else {
      paymentDate = date;
    }
    
    const now = new Date();

    switch (dateFilter) {
      case 'week':
        const weekAgo = new Date();
        weekAgo.setDate(weekAgo.getDate() - 7);
        return paymentDate >= weekAgo && paymentDate <= now;

      case 'month':
        const monthAgo = new Date();
        monthAgo.setMonth(monthAgo.getMonth() - 1);
        return paymentDate >= monthAgo && paymentDate <= now;

      case '3months':
        const threeMonthsAgo = new Date();
        threeMonthsAgo.setMonth(threeMonthsAgo.getMonth() - 3);
        return paymentDate >= threeMonthsAgo && paymentDate <= now;

      case '6months':
        const sixMonthsAgo = new Date();
        sixMonthsAgo.setMonth(sixMonthsAgo.getMonth() - 6);
        return paymentDate >= sixMonthsAgo && paymentDate <= now;

      case 'year':
        const yearAgo = new Date();
        yearAgo.setFullYear(yearAgo.getFullYear() - 1);
        return paymentDate >= yearAgo && paymentDate <= now;

      case 'custom':
        if (!startDate || !endDate) return true;
        const start = new Date(startDate);
        const end = new Date(endDate);
        end.setHours(23, 59, 59, 999); // Include the entire end date
        return paymentDate >= start && paymentDate <= end;

      default:
        return true;
    }
  };

  const formatCurrency = (amount: string | number) => {
    const num = typeof amount === 'string' ? parseFloat(amount) : amount;
    if (isNaN(num)) return '0 €';
    return new Intl.NumberFormat('fr-FR', { style: 'currency', currency: 'EUR' }).format(num);
  };

  const calculatePaymentAmount = (rate: string, percentage: number) => {
    const totalAmount = parseFloat(rate);
    // Calculer le montant correspondant au pourcentage (ex: 70% de 5000€ = 3500€)
    return isNaN(totalAmount) ? 0 : totalAmount * (percentage / 100);
  };

  const filteredPayments = payments.filter(payment => {
    const matchesSearch =
      (payment.fullName || '').toLowerCase().includes(search.toLowerCase()) ||
      (payment.campaignName || '').toLowerCase().includes(search.toLowerCase()) ||
      (payment.brand || '').toLowerCase().includes(search.toLowerCase());

    const matchesDate = isDateInRange(payment.date);

    return matchesSearch && matchesDate;
  });

  // Pas besoin de trier à nouveau ici car les paiements sont déjà triés dans loadData

  const totalPending = filteredPayments
    .filter(payment => !payment.paid && payment.campaignId && collabs.find(c => c.id === payment.campaignId)?.payment_received)
    .reduce((sum, payment) => {
      // Calculer le montant à facturer selon le pourcentage
      const amountToPay = calculatePaymentAmount(payment.rate, payment.paidPercentage || 0);
      return sum + amountToPay;
    }, 0);

  const totalPaid = filteredPayments
    .filter(payment => payment.paid)
    .reduce((sum, payment) => {
      return sum + calculatePaymentAmount(payment.rate, payment.paidPercentage || 0);
    }, 0);

  const getPaymentKey = (payment: InfluencerPayment) => 
    `${payment.campaignId}-${payment.fullName}`;

  const getDateFilterLabel = () => {
    switch (dateFilter) {
      case 'week':
        return '7 derniers jours';
      case 'month':
        return '30 derniers jours';
      case '3months':
        return '3 derniers mois';
      case '6months':
        return '6 derniers mois';
      case 'year':
        return '12 derniers mois';
      case 'custom':
        return 'Période personnalisée';
      default:
        return 'Toutes les dates';
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-emerald-500 to-emerald-600 flex items-center justify-center shadow-lg shadow-emerald-500/30">
            <Euro className="w-7 h-7 text-white" />
          </div>
          <div>
            <h1 className="text-3xl font-bold text-white">Comptabilité influenceur</h1>
            <p className="text-white/50 mt-0.5">Gérez les paiements des influenceurs</p>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div className="bg-white/[0.04] rounded-2xl p-6 border border-white/[0.08]">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-base font-semibold text-white/60">En attente</h3>
            <span className="text-3xl font-bold text-amber-400">{formatCurrency(totalPending)}</span>
          </div>
          <div className="flex items-center text-white/50">
            <ArrowRight className="w-4 h-4 mr-1.5" />
            <span className="text-sm font-medium">Paiements à effectuer</span>
          </div>
        </div>

        <div className="bg-white/[0.04] rounded-2xl p-6 border border-white/[0.08]">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-base font-semibold text-white/60">Payé</h3>
            <span className="text-3xl font-bold text-emerald-400">{formatCurrency(totalPaid)}</span>
          </div>
          <div className="flex items-center text-white/50">
            <ArrowRight className="w-4 h-4 mr-1.5" />
            <span className="text-sm font-medium">Paiements effectués</span>
          </div>
        </div>
      </div>

      <div className="bg-white/[0.04] rounded-2xl border border-white/[0.08] p-6">
        <div className="mb-6 flex flex-col sm:flex-row gap-4">
          <div className="flex-1">
            <div className="relative">
              <Search className="absolute left-3.5 top-1/2 transform -translate-y-1/2 w-5 h-5 text-white/30" />
              <input
                type="text"
                placeholder="Rechercher un paiement..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="w-full pl-11 pr-4 py-2.5 bg-white/[0.06] border border-white/[0.08] rounded-xl text-white placeholder-white/30 focus:ring-1 focus:ring-white/20 outline-none transition-shadow"
              />
            </div>
          </div>
          <button
            onClick={() => setIsFilterModalOpen(true)}
            className="flex items-center gap-2 px-4 py-2.5 bg-white/[0.08] text-white/60 rounded-xl hover:bg-white/[0.14] transition-all border border-white/[0.08] font-medium"
          >
            <Filter className="w-4 h-4" />
            {getDateFilterLabel()}
          </button>
        </div>

        {isLoading ? (
          <div className="flex items-center justify-center h-64">
            <div className="animate-spin rounded-full h-10 w-10 border-2 border-white/10 border-t-white/60"></div>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-white/[0.06] bg-white/[0.02]">
                  <th className="text-left py-4 px-4 text-[10px] font-semibold text-white/30 uppercase tracking-widest">Influenceur</th>
                  <th className="text-left py-4 px-4 text-[10px] font-semibold text-white/30 uppercase tracking-widest">Campagne</th>
                  <th className="text-left py-4 px-4 text-[10px] font-semibold text-white/30 uppercase tracking-widest">Marque</th>
                  <th className="text-left py-4 px-4 text-[10px] font-semibold text-white/30 uppercase tracking-widest">Date</th>
                  <th className="text-left py-4 px-4 text-[10px] font-semibold text-white/30 uppercase tracking-widest">Montant total</th>
                  <th className="text-left py-4 px-4 text-[10px] font-semibold text-white/30 uppercase tracking-widest">% à facturer</th>
                  <th className="text-left py-4 px-4 text-[10px] font-semibold text-white/30 uppercase tracking-widest">Montant à facturer</th>
                  <th className="text-left py-4 px-4 text-[10px] font-semibold text-white/30 uppercase tracking-widest">Statut</th>
                  <th className="text-right py-4 px-4 text-[10px] font-semibold text-white/30 uppercase tracking-widest">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-white/[0.04]">
                {filteredPayments.map((payment) => {
                  const paymentKey = getPaymentKey(payment);
                  const isEditing = editingPayment === paymentKey;
                  const amountToPay = calculatePaymentAmount(payment.rate, payment.paidPercentage || 0);

                  return (
                    <tr key={paymentKey} className="hover:bg-white/[0.04] transition-colors">
                      <td className="py-4 px-4 text-white font-medium">{payment.fullName}</td>
                      <td className="py-4 px-4 text-white/50">{payment.campaignName}</td>
                      <td className="py-4 px-4">
                        <div className="flex items-center gap-2 text-white/50">
                          <div className="w-7 h-7 rounded-lg bg-white/[0.06] flex items-center justify-center">
                            <Building2 className="w-4 h-4 text-white/40" />
                          </div>
                          <span>{payment.brand}</span>
                        </div>
                      </td>
                      <td className="py-4 px-4 text-white/50 text-sm">{formatDate(payment.date)}</td>
                      <td className="py-4 px-4 font-semibold text-white">{formatCurrency(payment.rate)}</td>
                      <td className="py-4 px-4">
                        {isEditing ? (
                          <div className="flex items-center gap-2">
                            <input
                              type="number"
                              className="w-20 text-center bg-white/[0.06] border border-white/[0.08] rounded-lg px-2 py-1 focus:ring-1 focus:ring-white/20 outline-none text-white"
                              value={tempPercentage}
                             onChange={(e) => {
                               const value = parseInt(e.target.value) || 0;
                               setTempPercentage(Math.min(100, Math.max(0, value)));
                             }}
                              onKeyDown={(e) => {
                                if (e.key === 'Enter') {
                                  handleUpdatePercentage(payment, tempPercentage);
                                } else if (e.key === 'Escape') {
                                  setEditingPayment(null);
                                }
                              }}
                              autoFocus
                            />
                            <span className="text-white/50 font-medium">%</span>
                            <button
                              onClick={() => handleUpdatePercentage(payment, tempPercentage)}
                              className="p-1.5 rounded-lg bg-emerald-500/20 text-emerald-400 hover:bg-emerald-500/30 transition-all"
                            >
                              <Check className="w-4 h-4" />
                            </button>
                            <button
                              onClick={() => setEditingPayment(null)}
                              className="p-1.5 rounded-lg bg-red-500/20 text-red-400 hover:bg-red-500/30 transition-all"
                            >
                              <X className="w-4 h-4" />
                            </button>
                          </div>
                        ) : (
                          <button
                             onClick={() => {
                              setEditingPayment(paymentKey);
                              setTempPercentage(payment.paidPercentage || 0);
                            }}
                            className="hover:text-white transition-colors text-white/50 font-medium"
                          >
                            {payment.paidPercentage || 0}%
                          </button>
                        )}
                      </td>
                      <td className="py-4 px-4 font-bold text-white/80">
                        {formatCurrency(calculatePaymentAmount(payment.rate, payment.paidPercentage || 0))}
                      </td>
                      <td className="py-4 px-4">
                        {payment.paid ? (
                          <span className="inline-flex items-center px-3 py-1 rounded-lg text-xs font-semibold bg-emerald-500/20 text-emerald-300 border border-emerald-500/20">
                            Payé
                          </span>
                        ) : (
                          <span className="inline-flex items-center px-3 py-1 rounded-lg text-xs font-semibold bg-amber-500/20 text-amber-300 border border-amber-500/20">
                            En attente
                          </span>
                        )}
                      </td>
                      <td className="py-4 px-4 text-right">
                        {!isEditing && (
                          <button
                            onClick={() => handleTogglePaidStatus(payment)}
                            className={`p-2 rounded-lg transition-all ${payment.paid ?
                              "bg-amber-500/20 text-amber-400 hover:bg-amber-500/30" :
                              "bg-emerald-500/20 text-emerald-400 hover:bg-emerald-500/30"
                            }`}
                            title={payment.paid ? "Marquer comme en attente" : "Marquer comme payé"}
                          >
                            {payment.paid ? <X className="w-5 h-5" /> : <Check className="w-5 h-5" />}
                          </button>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Date Filter Modal */}
      <Dialog
        open={isFilterModalOpen}
        onClose={() => setIsFilterModalOpen(false)}
        className="fixed inset-0 z-50 overflow-y-auto"
      >
        <div className="flex items-center justify-center min-h-screen p-4">
          <Dialog.Overlay className="fixed inset-0 bg-black/60 backdrop-blur-sm" />
          <div className="relative bg-[#141414] border border-white/[0.08] rounded-2xl max-w-md w-full">
            <div className="p-6 border-b border-white/[0.08]">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-white/[0.06] flex items-center justify-center">
                  <Filter className="w-5 h-5 text-white/60" />
                </div>
                <h3 className="text-xl font-bold text-white">Filtrer par date</h3>
              </div>
            </div>

            <div className="p-6 space-y-3">
              <button
                onClick={() => {
                  setDateFilter('all');
                  setIsFilterModalOpen(false);
                }}
                className={`w-full text-left px-4 py-2.5 rounded-xl font-medium transition-all ${
                  dateFilter === 'all' ? 'bg-white text-black' : 'bg-white/[0.08] text-white/60 hover:bg-white/[0.14] border border-white/[0.08]'
                }`}
              >
                Toutes les dates
              </button>
              <button
                onClick={() => {
                  setDateFilter('week');
                  setIsFilterModalOpen(false);
                }}
                className={`w-full text-left px-4 py-2.5 rounded-xl font-medium transition-all ${
                  dateFilter === 'week' ? 'bg-white text-black' : 'bg-white/[0.08] text-white/60 hover:bg-white/[0.14] border border-white/[0.08]'
                }`}
              >
                7 derniers jours
              </button>
              <button
                onClick={() => {
                  setDateFilter('month');
                  setIsFilterModalOpen(false);
                }}
                className={`w-full text-left px-4 py-2.5 rounded-xl font-medium transition-all ${
                  dateFilter === 'month' ? 'bg-white text-black' : 'bg-white/[0.08] text-white/60 hover:bg-white/[0.14] border border-white/[0.08]'
                }`}
              >
                30 derniers jours
              </button>
              <button
                onClick={() => {
                  setDateFilter('3months');
                  setIsFilterModalOpen(false);
                }}
                className={`w-full text-left px-4 py-2.5 rounded-xl font-medium transition-all ${
                  dateFilter === '3months' ? 'bg-white text-black' : 'bg-white/[0.08] text-white/60 hover:bg-white/[0.14] border border-white/[0.08]'
                }`}
              >
                3 derniers mois
              </button>
              <button
                onClick={() => {
                  setDateFilter('6months');
                  setIsFilterModalOpen(false);
                }}
                className={`w-full text-left px-4 py-2.5 rounded-xl font-medium transition-all ${
                  dateFilter === '6months' ? 'bg-white text-black' : 'bg-white/[0.08] text-white/60 hover:bg-white/[0.14] border border-white/[0.08]'
                }`}
              >
                6 derniers mois
              </button>
              <button
                onClick={() => {
                  setDateFilter('year');
                  setIsFilterModalOpen(false);
                }}
                className={`w-full text-left px-4 py-2.5 rounded-xl font-medium transition-all ${
                  dateFilter === 'year' ? 'bg-white text-black' : 'bg-white/[0.08] text-white/60 hover:bg-white/[0.14] border border-white/[0.08]'
                }`}
              >
                12 derniers mois
              </button>

              <div className="border-t border-white/[0.08] pt-4">
                <h4 className="text-sm font-semibold text-white mb-3">Période personnalisée</h4>
                <div className="space-y-3">
                  <div>
                    <label className="block text-sm font-medium text-white/60 mb-1.5">Date de début</label>
                    <input
                      type="date"
                      value={startDate}
                      onChange={(e) => setStartDate(e.target.value)}
                      className="w-full bg-white/[0.06] border border-white/[0.08] rounded-xl px-3 py-2 text-white focus:ring-1 focus:ring-white/20 outline-none"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-white/60 mb-1.5">Date de fin</label>
                    <input
                      type="date"
                      value={endDate}
                      onChange={(e) => setEndDate(e.target.value)}
                      className="w-full bg-white/[0.06] border border-white/[0.08] rounded-xl px-3 py-2 text-white focus:ring-1 focus:ring-white/20 outline-none"
                    />
                  </div>
                  <button
                    onClick={() => {
                      if (startDate && endDate) {
                        setDateFilter('custom');
                        setIsFilterModalOpen(false);
                      }
                    }}
                    disabled={!startDate || !endDate}
                    className="w-full px-4 py-2.5 bg-white text-black rounded-xl hover:bg-white/90 font-semibold disabled:opacity-50 disabled:cursor-not-allowed transition-all"
                  >
                    Appliquer la période
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </Dialog>
    </div>
  );
}